import { useEffect, useRef } from 'react'

/**
 * Drifts an element against the scroll. `strength` is the total travel in px
 * across a full viewport pass; negative values drift the other way.
 *
 * Writes `transform` directly, so never put a Tailwind transform utility on the
 * same element.
 */
export const useParallax = <T extends HTMLElement>(strength = 40) => {
    const ref = useRef<T | null>(null)

    useEffect(() => {
        const el = ref.current
        if (!el) return
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

        let raf = 0
        let queued = false

        const update = () => {
            queued = false

            const rect = el.getBoundingClientRect()
            const viewport = window.innerHeight || 1
            const progress = (rect.top + rect.height / 2 - viewport / 2) / viewport

            el.style.transform = `translate3d(0, ${(progress * strength).toFixed(2)}px, 0)`
        }

        const onScroll = () => {
            if (queued) return
            queued = true
            raf = requestAnimationFrame(update)
        }

        update()
        window.addEventListener('scroll', onScroll, { passive: true })
        window.addEventListener('resize', onScroll)

        return () => {
            cancelAnimationFrame(raf)
            window.removeEventListener('scroll', onScroll)
            window.removeEventListener('resize', onScroll)
            el.style.transform = ''
        }
    }, [strength])

    return ref
}
